(function () {
  "use strict";

  // Guard against multi-execution. cclp-assets.liquid is rendered from every
  // cclp-* section, which inserts the <script src="cclp.js"> tag once per
  // section. Browsers cache the file fetch but each tag still EXECUTES the
  // IIFE — so without this guard, every event handler is attached N times
  // (currently ~18). Idempotent handlers (tier picker, modal open) survived;
  // the toggle in initFlavorPicker did not (even N → state never flips).
  if (window.__cclpInit) return;
  window.__cclpInit = true;

  // Accordion: click trigger → toggle aria-expanded + show/hide body
  function initAccordions() {
    const triggers = document.querySelectorAll(".cclp-accordion-trigger");
    triggers.forEach(function (trigger) {
      trigger.addEventListener("click", function () {
        const isOpen = trigger.getAttribute("aria-expanded") === "true";
        trigger.setAttribute("aria-expanded", String(!isOpen));
        const body = trigger.nextElementSibling;
        if (!body) return;
        if (isOpen) {
          body.setAttribute("hidden", "");
        } else {
          body.removeAttribute("hidden");
        }
      });
    });
  }

  // Smooth-scroll for in-page anchors (e.g., Final CTA → #tier-picker)
  function initSmoothScroll() {
    const anchors = document.querySelectorAll('a[href^="#"]');
    anchors.forEach(function (a) {
      const href = a.getAttribute("href");
      if (!href || href === "#") return;
      a.addEventListener("click", function (e) {
        const target = document.querySelector(href);
        if (!target) return;
        e.preventDefault();
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    });
  }

  // Meta / TW AddToCart event on permalink CTA click.
  // Only fires if window.fbq or window.TriplePixel exist (global theme snippet installed them).
  function initAddToCartTracking() {
    const ctas = document.querySelectorAll(".cclp-btn[data-cclp-permalink]");
    ctas.forEach(function (cta) {
      cta.addEventListener("click", function () {
        try {
          if (typeof window.fbq === "function") {
            window.fbq("track", "AddToCart");
          }
          if (window.TriplePixel && typeof window.TriplePixel === "function") {
            window.TriplePixel("AddToCart");
          }
        } catch (err) {
          // Swallow — tracking failure should never block checkout navigation.
        }
      });
    });
  }

  // Reactive tier picker (Javvy-style):
  //   - tier card click → preventDefault, set aria-checked="true" on the chosen
  //     card, "false" on siblings, add .cclp-tier-card--selected
  //   - propagate the chosen tier's data-tier-level into the gift row's
  //     data-selected-tier-qty attribute — drives the free-gift row's
  //     locked/unlocked CSS. tier_level is the unlock dial (1/2/3),
  //     decoupled from qty_1 (cart line-item count).
  //   - copy the chosen tier's data-permalink onto the main CTA href so users
  //     who click the big CTA hit the right cart permalink
  function initTierPickers() {
    const groups = document.querySelectorAll(".cclp-tier-grid--reactive");
    groups.forEach(function (group) {
      const cards = group.querySelectorAll(".cclp-tier-card--quantity");
      const section = group.closest(".cclp-commerce-hero");
      if (!section) return;
      const giftRow = section.querySelector(".cclp-gift-row");
      const mainCta = section.querySelector(".cclp-main-cta");

      cards.forEach(function (card) {
        card.addEventListener("click", function (e) {
          e.preventDefault();

          cards.forEach(function (c) {
            c.setAttribute("aria-checked", c === card ? "true" : "false");
            c.classList.toggle("cclp-tier-card--selected", c === card);
          });

          const tierLevel = card.getAttribute("data-tier-level");
          if (giftRow && tierLevel) {
            giftRow.setAttribute("data-selected-tier-qty", tierLevel);
          }

          const permalink = card.getAttribute("data-permalink");
          if (mainCta && permalink && permalink !== "#") {
            mainCta.setAttribute("href", permalink);
          }
        });

        // Keyboard support — Enter / Space activates the radio
        card.addEventListener("keydown", function (e) {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            card.click();
          }
        });
      });
    });
  }

  // Flavor picker state machine (step 2 of the commerce hero):
  //   - Main CTA click (when data-flavor-picker-enabled="true") → hide tier step,
  //     show flavor step, populate tier summary + instruction text
  //   - Flavor card click → toggle aria-checked, FIFO-replace if over limit,
  //     enable/disable subscribe CTA based on flavor_count_required
  //   - Subscribe CTA click → href is pre-built from selected flavor variants
  //     × pack_count_per_flavor + tier extras_csv + selling_plan_id, fires
  //     AddToCart pixel, then lets browser navigate
  //   - Back button → reset state, return to tier step
  function initFlavorPicker() {
    const heroes = document.querySelectorAll(".cclp-commerce-hero");
    heroes.forEach(function (hero) {
      const flavorStep = hero.querySelector('[data-step="flavor"]');
      const tierStep = hero.querySelector('[data-step="tier"]');
      if (!flavorStep || !tierStep) return;

      const mainCta = hero.querySelector("[data-cclp-main-cta]");
      const backBtn = flavorStep.querySelector("[data-flavor-back]");
      const flavorCards = flavorStep.querySelectorAll(".cclp-flavor-card");
      const flavorCta = flavorStep.querySelector("[data-flavor-cta]");
      const tierSummaryLabel = flavorStep.querySelector("[data-flavor-tier-summary-label]");
      const tierSummaryPrice = flavorStep.querySelector("[data-flavor-tier-summary-price]");
      const cupSubgrid = flavorStep.querySelector('[data-flavor-subgrid="cup"]');
      const instructionPacket = flavorStep.querySelector("[data-flavor-instruction-packet]");
      const instructionCup = flavorStep.querySelector("[data-flavor-instruction-cup]");

      let activeTier = null;

      function gatherTierData(card) {
        return {
          name: card.getAttribute("data-tier-name") || "",
          qty: parseInt(card.getAttribute("data-tier-qty") || "1", 10),
          price: card.getAttribute("data-tier-price") || "",
          flavorCount: parseInt(card.getAttribute("data-flavor-count-required") || "1", 10),
          packCount: parseInt(card.getAttribute("data-pack-count-per-flavor") || "6", 10),
          cupPackCount: parseInt(card.getAttribute("data-cup-pack-count") || "0", 10),
          extras: card.getAttribute("data-extras-csv") || "",
          gifts: card.getAttribute("data-gifts-csv") || "",
          giftMatchPickedCupQty: parseInt(card.getAttribute("data-gift-match-picked-cup-qty") || "0", 10),
          bxgyDiscountCode: card.getAttribute("data-bxgy-discount-code") || "",
          tierTrackId: card.getAttribute("data-tier-track-id") || "",
          planId: card.getAttribute("data-selling-plan-id") || ""
        };
      }

      function getSelectedTierCard() {
        return (
          tierStep.querySelector(".cclp-tier-card--selected") ||
          tierStep.querySelector(".cclp-tier-card--quantity")
        );
      }

      function isDualMode() {
        return !!(activeTier && activeTier.cupPackCount > 0);
      }

      function getCheckedFlavors(mode) {
        return Array.from(flavorCards).filter(function (c) {
          if (c.getAttribute("aria-checked") !== "true") return false;
          if (mode) return c.getAttribute("data-flavor-mode") === mode;
          return true;
        });
      }

      // R22 — Subscription items only in cart-permalink (subs all share the
      // same plan group, so cart-wide ?selling_plan applies cleanly). Gifts
      // are added separately via /cart/add.js so Shopify doesn't drop the
      // plan from the whole cart due to a non-plan variant.
      // Returns { url, gifts: [{id, quantity}, ...] }.
      function buildCheckoutPlan() {
        if (!activeTier) return { url: "#", gifts: [] };
        const parts = [];
        // R23 (Kevin May 5) — supports multi-flavor selection. T3 in V3
        // lets the user pick 2 different packet flavors. Iterate over ALL
        // checked packet cards (was: only first). Each picked flavor
        // gets `packCount` units of its variant added to the cart.
        const packetChecked = getCheckedFlavors("packet");
        packetChecked.forEach(function (card) {
          const vid = card.getAttribute("data-variant-id");
          if (vid) parts.push(vid + ":" + activeTier.packCount);
        });
        if (isDualMode()) {
          const cupChecked = getCheckedFlavors("cup");
          cupChecked.forEach(function (card) {
            const vid = card.getAttribute("data-variant-id");
            if (vid) parts.push(vid + ":" + activeTier.cupPackCount);
          });
        }
        if (parts.length === 0) return { url: "#", gifts: [] };
        let li = parts.join(",");
        if (activeTier.extras) li = li + "," + activeTier.extras;
        let url = "/cart/" + li;
        if (activeTier.planId) {
          url += "?selling_plan=" + activeTier.planId + "&return_to=/checkout";
        } else {
          url += "?return_to=/checkout";
        }
        const gifts = parseCsvLineItems(activeTier.gifts);
        // R23 (Kevin May 5) — V3 T3 FOUNDERS: bonus 6-pack of the
        // user's CHOSEN cup flavor as a free gift. Reads the picked cup
        // variant + appends N copies as no-plan gift line items.
        if (activeTier.giftMatchPickedCupQty > 0 && isDualMode()) {
          const cupChecked = getCheckedFlavors("cup");
          if (cupChecked.length > 0) {
            const cupVid = parseInt(cupChecked[0].getAttribute("data-variant-id"), 10);
            if (cupVid) {
              gifts.push({ id: cupVid, quantity: activeTier.giftMatchPickedCupQty });
            }
          }
        }
        return { url: url, gifts: gifts };
      }

      function parseCsvLineItems(csv) {
        if (!csv) return [];
        return csv.split(",").map(function (entry) {
          const trimmed = entry.trim();
          if (!trimmed) return null;
          const segs = trimmed.split(":");
          const id = parseInt(segs[0], 10);
          const qty = parseInt(segs[1] || "1", 10);
          if (!id || !qty) return null;
          return { id: id, quantity: qty };
        }).filter(function (x) { return x !== null; });
      }

      // Back-compat alias: existing code path uses buildPermalink() for the
      // CTA href. Now returns subscription-only permalink string.
      function buildPermalink() {
        return buildCheckoutPlan().url;
      }

      function updateFlavorCta() {
        if (!flavorCta || !activeTier) return;
        // R23 (Kevin May 5) — packet picker now respects flavor_count_required.
        // T3 in V3 needs 2 packet flavors picked; T1/T2 still need 1.
        const packetTarget = activeTier.flavorCount > 0 ? activeTier.flavorCount : 1;
        const packetReady = getCheckedFlavors("packet").length >= packetTarget;
        const cupReady = !isDualMode() || getCheckedFlavors("cup").length >= 1;
        const ready = packetReady && cupReady;
        flavorCta.setAttribute("aria-disabled", ready ? "false" : "true");
        flavorCta.setAttribute("href", ready ? buildPermalink() : "#");
        if (instructionPacket) {
          const n = getCheckedFlavors("packet").length;
          const word = packetTarget === 1 ? "packet flavor selected" : "packet flavors selected";
          instructionPacket.textContent = n + " of " + packetTarget + " " + word;
        }
        if (instructionCup && isDualMode()) {
          const n = getCheckedFlavors("cup").length;
          instructionCup.textContent = n + " of 1 cup flavor selected";
        }
      }

      function showFlavorStep() {
        const selected = getSelectedTierCard();
        if (!selected) return;
        activeTier = gatherTierData(selected);

        if (tierSummaryLabel) {
          const totalPacks = activeTier.qty * activeTier.packCount;
          tierSummaryLabel.textContent =
            totalPacks + " packs — " + activeTier.name;
        }
        if (tierSummaryPrice && activeTier.price) {
          tierSummaryPrice.textContent = "$" + activeTier.price;
        }

        // Show/hide cup sub-picker per tier mode + flag step for CSS hooks
        if (cupSubgrid) {
          if (isDualMode()) {
            cupSubgrid.removeAttribute("hidden");
            flavorStep.classList.add("cclp-flavor-picker--dual");
          } else {
            cupSubgrid.setAttribute("hidden", "");
            flavorStep.classList.remove("cclp-flavor-picker--dual");
          }
        }

        // Reset selections
        flavorCards.forEach(function (c) {
          c.setAttribute("aria-checked", "false");
          c.classList.remove("cclp-flavor-card--selected");
        });
        updateFlavorCta();

        tierStep.setAttribute("hidden", "");
        flavorStep.removeAttribute("hidden");
        flavorStep.scrollIntoView({ behavior: "smooth", block: "start" });
      }

      function showTierStep() {
        flavorStep.setAttribute("hidden", "");
        tierStep.removeAttribute("hidden");
        tierStep.scrollIntoView({ behavior: "smooth", block: "start" });
      }

      if (mainCta) {
        mainCta.addEventListener("click", function (e) {
          const enabled = mainCta.getAttribute("data-flavor-picker-enabled") === "true";
          if (!enabled) return;
          e.preventDefault();
          showFlavorStep();
        });
      }

      if (backBtn) {
        backBtn.addEventListener("click", function (e) {
          e.preventDefault();
          showTierStep();
        });
      }

      flavorCards.forEach(function (card) {
        const cardMode = card.getAttribute("data-flavor-mode") || "packet";
        card.addEventListener("click", function (e) {
          e.preventDefault();
          const isChecked = card.getAttribute("aria-checked") === "true";
          if (isChecked) {
            card.setAttribute("aria-checked", "false");
            card.classList.remove("cclp-flavor-card--selected");
          } else {
            // R23 (Kevin May 5) — FIFO replacement when limit reached.
            // Limit = activeTier.flavorCount for packet mode (T3 V3 = 2),
            // 1 for cup mode (always single cup pick).
            const limit = cardMode === "packet" && activeTier && activeTier.flavorCount > 0
              ? activeTier.flavorCount
              : 1;
            const currentlyChecked = getCheckedFlavors(cardMode);
            if (currentlyChecked.length >= limit) {
              const first = currentlyChecked[0];
              first.setAttribute("aria-checked", "false");
              first.classList.remove("cclp-flavor-card--selected");
            }
            card.setAttribute("aria-checked", "true");
            card.classList.add("cclp-flavor-card--selected");
          }
          updateFlavorCta();
        });

        card.addEventListener("keydown", function (e) {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            card.click();
          }
        });
      });

      if (flavorCta) {
        flavorCta.addEventListener("click", function (e) {
          if (flavorCta.getAttribute("aria-disabled") === "true") {
            e.preventDefault();
            return;
          }
          try {
            if (typeof window.fbq === "function") window.fbq("track", "AddToCart");
            if (window.TriplePixel && typeof window.TriplePixel === "function") {
              window.TriplePixel("AddToCart");
            }
          } catch (err) {
            // tracking failure should never block checkout navigation
          }
          // R22 — Always intercept: Shopify's `/cart/v:q?selling_plan=X`
          // permalink does NOT actually apply the plan (verified via curl —
          // selling_plan_allocation comes back null). The ONLY path that
          // attaches the plan is /cart/add.js with selling_plan in the body.
          // So we ALWAYS take the AJAX path, even when there are no gifts.
          const plan = buildCheckoutPlan();
          e.preventDefault();
          // R22 — Stop Rebuy's SmartCart drawer from popping out during the
          // cart-rebuild → /checkout redirect. CSS hides it visually; this
          // also calls Rebuy's API to short-circuit the open if available,
          // and disables auto-open listeners.
          try {
            if (window.Rebuy && window.Rebuy.SmartCart) {
              if (typeof window.Rebuy.SmartCart.disable === "function") window.Rebuy.SmartCart.disable();
              if (typeof window.Rebuy.SmartCart.hide === "function") window.Rebuy.SmartCart.hide();
              if (typeof window.Rebuy.SmartCart.close === "function") window.Rebuy.SmartCart.close();
            }
          } catch (rerr) { /* never block checkout for Rebuy errors */ }
          // Step 1: clear cart so existing items don't pollute
          fetch("/cart/clear.js", { method: "POST", credentials: "same-origin" })
            .then(function () {
              // Step 2: add subscription items by visiting the cart permalink
              // BUT the permalink redirects to /checkout. Instead, use
              // /cart/add.js for both subs and gifts so we control flow.
              const subItems = [];
              // R23 (Kevin May 5) — multi-flavor support. Iterate ALL
              // checked packet cards (was: only [0]). T3 V3 picks 2.
              const planIdNum = activeTier.planId ? parseInt(activeTier.planId, 10) : undefined;
              const packetChecked = getCheckedFlavors("packet");
              packetChecked.forEach(function (card) {
                const vid = parseInt(card.getAttribute("data-variant-id"), 10);
                if (vid) subItems.push({ id: vid, quantity: activeTier.packCount, selling_plan: planIdNum });
              });
              if (isDualMode()) {
                const cupChecked = getCheckedFlavors("cup");
                cupChecked.forEach(function (card) {
                  const vid = parseInt(card.getAttribute("data-variant-id"), 10);
                  if (vid) subItems.push({ id: vid, quantity: activeTier.cupPackCount, selling_plan: planIdNum });
                });
              }
              const subExtras = parseCsvLineItems(activeTier.extras);
              subExtras.forEach(function (it) {
                if (activeTier.planId) it.selling_plan = parseInt(activeTier.planId, 10);
                subItems.push(it);
              });
              const allItems = subItems.concat(plan.gifts);
              return fetch("/cart/add.js", {
                method: "POST",
                credentials: "same-origin",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ items: allItems })
              });
            })
            .then(function (resp) {
              if (!resp || !resp.ok) {
                // Surface error and fall back to plain permalink so user
                // can still get to checkout, even if without proper subs.
                window.location = plan.url;
                return null;
              }
              return resp.json();
            })
            .then(function (data) {
              if (!data) return;
              // R23 (Kevin May 5) — analytics: tag the cart with an
              // lp_origin attribute so Shopify Flow can tag the customer
              // on order creation. Fire-and-forget — request flies in
              // parallel with the redirect so user-perceived latency is
              // ~0ms.
              try {
                const lpHandle = (window.location.pathname.match(/\/pages\/cclp-sub-([a-z0-9]+)/i) || [])[1] || "";
                const tierId = (activeTier && activeTier.tierTrackId) || "";
                if (lpHandle && tierId) {
                  fetch("/cart/update.js", {
                    method: "POST",
                    credentials: "same-origin",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      attributes: { lp_origin: lpHandle + "-" + tierId }
                    }),
                    keepalive: true
                  });
                }
              } catch (terr) { /* never block checkout for analytics */ }

              // R23 (Kevin May 5) — if this tier has a code-based BXGY
              // discount, route through Shopify's /discount/CODE endpoint
              // which auto-applies the code, then redirects to checkout.
              // Tiers without a code (e.g., T1 with no gifts) just go
              // straight to checkout.
              const code = (activeTier && activeTier.bxgyDiscountCode) || "";
              if (code) {
                window.location = "/discount/" + encodeURIComponent(code) + "?redirect=/checkout";
              } else {
                window.location = "/checkout";
              }
            })
            .catch(function () {
              window.location = plan.url;
            });
        });
      }
    });
  }

  // Round 18 — #4: custom progress rail tracks horizontal scroll on the
  // "See the slurp" carousel (replaces hidden native scrollbar).
  function initProductCarouselRail() {
    const tracks = document.querySelectorAll(
      "[data-cclp-product-carousel-track]"
    );
    tracks.forEach(function (track) {
      const rail = track.parentElement.querySelector(
        "[data-cclp-product-carousel-rail]"
      );
      if (!rail) return;
      const thumb = rail.querySelector(
        "[data-cclp-product-carousel-rail-thumb]"
      );
      if (!thumb) return;

      function updateThumb() {
        const visible = track.clientWidth;
        const total = track.scrollWidth;
        if (total <= visible + 1) {
          // Nothing to scroll — hide the rail entirely
          rail.style.display = "none";
          return;
        }
        rail.style.display = "";
        const ratio = visible / total;
        const thumbWidthPct = Math.max(15, Math.min(100, ratio * 100));
        const maxScroll = total - visible;
        const scrollPct =
          maxScroll > 0 ? (track.scrollLeft / maxScroll) * (100 - thumbWidthPct) : 0;
        thumb.style.width = thumbWidthPct + "%";
        thumb.style.left = scrollPct + "%";
      }

      updateThumb();
      track.addEventListener("scroll", updateThumb, { passive: true });
      window.addEventListener("resize", updateThumb);
    });
  }

  // Round 18 — #6: on mobile, make the entire flavor-profile card tap to
  // open the nutrition modal (button alone is too small a hit target).
  function initFlavorCardMobileTap() {
    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    if (!isMobile) return;
    const items = document.querySelectorAll(".cclp-flavor-profile__item");
    items.forEach(function (item) {
      const trigger = item.querySelector("[data-cclp-nutrition-trigger]");
      if (!trigger) return;
      item.classList.add("cclp-flavor-profile__item--tappable");
      item.addEventListener("click", function (e) {
        // Don't double-fire if the actual button was tapped.
        if (e.target.closest("[data-cclp-nutrition-trigger]")) return;
        trigger.click();
      });
    });
  }

  // R23 (Kevin May 5) — Single-tap play/pause for UGC videos.
  // We removed native controls (`controls: false` in cclp-video-player.liquid)
  // because iOS Safari was double-tap-required when controls=true (first tap
  // showed controls, second tap played). With controls off, ANY tap on the
  // video triggers play/pause via this handler.
  // Adds an `is-playing` data attribute so CSS can hide/show the play
  // button overlay accordingly.
  function initUgcVideoFullTap() {
    const videos = document.querySelectorAll(".cclp-ugc-strip video");
    videos.forEach(function (video) {
      video.setAttribute("playsinline", "");
      video.removeAttribute("controls");
      video.dataset.cclpVideoState = "paused";
      function toggle(e) {
        if (e) e.preventDefault();
        if (video.paused) {
          video.play().then(function () {
            video.dataset.cclpVideoState = "playing";
          }).catch(function () {});
        } else {
          video.pause();
          video.dataset.cclpVideoState = "paused";
        }
      }
      video.addEventListener("click", toggle);
      video.addEventListener("play", function () {
        video.dataset.cclpVideoState = "playing";
      });
      video.addEventListener("pause", function () {
        video.dataset.cclpVideoState = "paused";
      });
      video.addEventListener("ended", function () {
        video.dataset.cclpVideoState = "paused";
      });
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      initAccordions();
      initSmoothScroll();
      initAddToCartTracking();
      initPrepaidToggle();
      initTierPickers();
      initFlavorPicker();
      initHeroCarousel();
      initNutritionModal();
      initProductCarouselRail();
      initFlavorCardMobileTap();
      initUgcVideoFullTap();
    });
  } else {
    initAccordions();
    initSmoothScroll();
    initAddToCartTracking();
    initPrepaidToggle();
    initTierPickers();
    initFlavorPicker();
    initStickyCta();
    initGiftRowReactivity();
    initHeroCarousel();
    initNutritionModal();
    initProductCarouselRail();
    initFlavorCardMobileTap();
    initUgcVideoFullTap();
  }

  // Round 12 — Sticky CTA pill (mobile only).
  // R23 (Kevin May 5) — sticky CTA now ALWAYS scrolls back to the
  // commerce-hero section (where the tier picker lives). It used to
  // mirror the mainCta's href, but mainCta points to a cart permalink —
  // tapping the sticky CTA submitted a one-time $39 cart bypass instead
  // of taking the user back up to pick a tier + flavor. Anchor scroll is
  // the correct UX for "scroll to subscribe" sticky pills.
  function initStickyCta() {
    if (window.innerWidth >= 1024) return;
    const mainCta = document.querySelector(".cclp-commerce-hero .cclp-main-cta");
    if (!mainCta) return;
    const sticky = document.createElement("a");
    sticky.className = "cclp-sticky-cta";
    sticky.setAttribute("data-cclp-sticky", "");
    sticky.href = "#commerce-hero";
    sticky.textContent = (mainCta.textContent || "Subscribe Now").trim();
    sticky.addEventListener("click", function (e) {
      const target = document.querySelector("#commerce-hero");
      if (target) {
        e.preventDefault();
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
    document.body.appendChild(sticky);

    // Keep the LABEL synced to mainCta text but no longer sync href.
    const syncHref = function () { /* href is fixed to anchor */ };
    new MutationObserver(syncHref).observe(mainCta, { attributes: true, attributeFilter: ["href"] });
    new MutationObserver(function () {
      sticky.textContent = (mainCta.textContent || "Subscribe Now").trim();
    }).observe(mainCta, { childList: true, subtree: true, characterData: true });

    if ("IntersectionObserver" in window) {
      const io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          // Show sticky once the original CTA has scrolled out of viewport top
          const offscreen = !e.isIntersecting && e.boundingClientRect.top < 0;
          sticky.classList.toggle("cclp-sticky-cta--visible", offscreen);
        });
      }, { threshold: 0, rootMargin: "0px 0px -40px 0px" });
      io.observe(mainCta);
    } else {
      // Fallback: scroll listener (throttled)
      let ticking = false;
      window.addEventListener("scroll", function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          const r = mainCta.getBoundingClientRect();
          sticky.classList.toggle("cclp-sticky-cta--visible", r.bottom < 0);
          ticking = false;
        });
      }, { passive: true });
    }
  }

  // Round 12 — Gift row reactivity (2A gift value stamp + 2D reveal animation).
  // Observes data-selected-tier-qty changes on .cclp-gift-row and:
  //   - sums data-retail-value of unlocked .cclp-gift-cell into the value stamp
  //   - flags newly-unlocked cards with .cclp-gift-card--just-unlocked for one cycle
  function initGiftRowReactivity() {
    const rows = document.querySelectorAll(".cclp-gift-row");
    if (!rows.length) return;

    function parseValue(raw) {
      if (!raw) return 0;
      const m = String(raw).match(/[\d.]+/);
      return m ? parseFloat(m[0]) : 0;
    }

    function isUnlocked(cell, selectedQty) {
      const minQty = parseInt(cell.getAttribute("data-min-qty") || "1", 10);
      return selectedQty >= minQty;
    }

    function update(row, prevQty) {
      const selectedQty = parseInt(row.getAttribute("data-selected-tier-qty") || "1", 10);
      const cells = row.querySelectorAll(".cclp-gift-cell");
      let total = 0;
      cells.forEach(function (cell) {
        const unlockedNow = isUnlocked(cell, selectedQty);
        if (unlockedNow) {
          total += parseValue(cell.getAttribute("data-retail-value"));
        }
        if (prevQty != null) {
          const unlockedBefore = isUnlocked(cell, prevQty);
          if (unlockedNow && !unlockedBefore) {
            const card = cell.querySelector(".cclp-gift-card");
            if (card) {
              card.classList.remove("cclp-gift-card--just-unlocked");
              // force reflow so animation re-runs
              void card.offsetWidth;
              card.classList.add("cclp-gift-card--just-unlocked");
              setTimeout(function () {
                card.classList.remove("cclp-gift-card--just-unlocked");
              }, 700);
            }
          }
        }
      });
      const stamp = row.parentElement && row.parentElement.querySelector("[data-cclp-gift-value-stamp]");
      if (stamp) {
        stamp.textContent = total > 0
          ? "$" + Math.round(total) + " in free gift value"
          : "";
      }
    }

    rows.forEach(function (row) {
      let prevQty = parseInt(row.getAttribute("data-selected-tier-qty") || "1", 10);
      update(row, null);
      new MutationObserver(function () {
        update(row, prevQty);
        prevQty = parseInt(row.getAttribute("data-selected-tier-qty") || "1", 10);
      }).observe(row, { attributes: true, attributeFilter: ["data-selected-tier-qty"] });
    });
  }

  // Round 16 — Hero image carousel.
  // Native scroll-snap drives layout/swipe. Prev/next + dots are JS niceties.
  function initHeroCarousel() {
    const carousels = document.querySelectorAll("[data-cclp-hero-carousel]");
    carousels.forEach(function (carousel) {
      const track = carousel.querySelector("[data-cclp-hero-track]");
      if (!track) return;
      const slides = track.querySelectorAll("[data-cclp-hero-slide]");
      if (slides.length <= 1) return;
      const prev = carousel.querySelector("[data-cclp-hero-prev]");
      const next = carousel.querySelector("[data-cclp-hero-next]");
      const dots = carousel.querySelectorAll("[data-cclp-hero-dot]");

      function scrollToIndex(i) {
        const slide = slides[i];
        if (!slide) return;
        track.scrollTo({ left: slide.offsetLeft, behavior: "smooth" });
      }

      function currentIndex() {
        const center = track.scrollLeft + track.clientWidth / 2;
        let bestIdx = 0;
        let bestDist = Infinity;
        slides.forEach(function (s, i) {
          const slideCenter = s.offsetLeft + s.clientWidth / 2;
          const d = Math.abs(slideCenter - center);
          if (d < bestDist) {
            bestDist = d;
            bestIdx = i;
          }
        });
        return bestIdx;
      }

      function syncDots() {
        const i = currentIndex();
        dots.forEach(function (d, idx) {
          if (idx === i) {
            d.classList.add("cclp-hero-carousel__dot--active");
            d.setAttribute("aria-selected", "true");
          } else {
            d.classList.remove("cclp-hero-carousel__dot--active");
            d.setAttribute("aria-selected", "false");
          }
        });
      }

      if (prev) {
        prev.addEventListener("click", function () {
          const i = Math.max(0, currentIndex() - 1);
          scrollToIndex(i);
        });
      }
      if (next) {
        next.addEventListener("click", function () {
          const i = Math.min(slides.length - 1, currentIndex() + 1);
          scrollToIndex(i);
        });
      }
      dots.forEach(function (d) {
        d.addEventListener("click", function () {
          const idx = parseInt(d.getAttribute("data-cclp-hero-dot") || "0", 10);
          scrollToIndex(idx);
        });
      });

      let scrollTimer;
      track.addEventListener("scroll", function () {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(syncDots, 80);
      });

      syncDots();
    });
  }

  // Round 22 — Per-flavor nutrition facts modal, dual-panel.
  // One modal per flavor-profile section. Triggers carry separate Packets +
  // Cups image URLs as data attrs; modal shows whichever variant(s) are set.
  // If both are set: side-by-side labeled panels. If only one: single panel.
  function initNutritionModal() {
    const modals = document.querySelectorAll("[data-cclp-nutrition-modal]");
    modals.forEach(function (modal) {
      const section = modal.closest("section");
      if (!section) return;
      const triggers = section.querySelectorAll("[data-cclp-nutrition-trigger]");
      const overlay = modal.querySelector("[data-cclp-nutrition-overlay]");
      const closeBtn = modal.querySelector("[data-cclp-nutrition-close]");
      const titleEl = modal.querySelector("[data-cclp-nutrition-title]");
      const variantPackets = modal.querySelector('[data-cclp-nutrition-variant="packets"]');
      const variantCups = modal.querySelector('[data-cclp-nutrition-variant="cups"]');
      const targetPackets = modal.querySelector("[data-cclp-nutrition-target-packets]");
      const targetCups = modal.querySelector("[data-cclp-nutrition-target-cups]");
      let lastTrigger = null;

      function applyVariant(variantEl, imgEl, src, alt) {
        if (!variantEl || !imgEl) return false;
        if (src) {
          imgEl.setAttribute("src", src);
          imgEl.setAttribute("alt", alt);
          variantEl.removeAttribute("hidden");
          return true;
        }
        imgEl.setAttribute("src", "");
        variantEl.setAttribute("hidden", "");
        return false;
      }

      function open(trigger) {
        const srcPackets = trigger.getAttribute("data-cclp-nutrition-image-packets") || "";
        const srcCups = trigger.getAttribute("data-cclp-nutrition-image-cups") || "";
        const label = trigger.getAttribute("data-cclp-nutrition-label") || "Nutrition Facts";
        const hasPackets = applyVariant(variantPackets, targetPackets, srcPackets, label + " — packets");
        const hasCups = applyVariant(variantCups, targetCups, srcCups, label + " — cups");
        if (titleEl) titleEl.textContent = label;
        modal.setAttribute("data-cclp-nutrition-layout", hasPackets && hasCups ? "dual" : "single");
        modal.removeAttribute("hidden");
        document.body.classList.add("cclp-body--cclp-modal-open");
        lastTrigger = trigger;
        const focusable = modal.querySelector("[data-cclp-nutrition-close]");
        if (focusable) focusable.focus();
      }

      function close() {
        modal.setAttribute("hidden", "");
        document.body.classList.remove("cclp-body--cclp-modal-open");
        if (targetPackets) targetPackets.setAttribute("src", "");
        if (targetCups) targetCups.setAttribute("src", "");
        if (lastTrigger) {
          lastTrigger.focus();
          lastTrigger = null;
        }
      }

      triggers.forEach(function (t) {
        t.addEventListener("click", function (e) {
          e.preventDefault();
          open(t);
        });
      });
      if (overlay) overlay.addEventListener("click", close);
      if (closeBtn) closeBtn.addEventListener("click", close);
      document.addEventListener("keydown", function (e) {
        if (e.key === "Escape" && !modal.hasAttribute("hidden")) {
          close();
        }
      });
    });
  }

  // Prepaid toggle (Monthly | 3-mo prepaid)
  // Lives above the tier picker. Click flips:
  //   - aria-checked on the radio buttons
  //   - data-prepaid-mode on the wrapper (drives CSS visibility swap)
  //   - data-permalink + data-selling-plan-id + data-extras-csv on each tier card
  //     so the existing tier-picker / flavor-picker / main-cta code reads the
  //     prepaid plan when prepaid is active.
  // Persists choice in localStorage.
  function initPrepaidToggle() {
    const toggles = document.querySelectorAll("[data-cclp-prepaid-toggle]");
    toggles.forEach(function (toggle) {
      const wrapper = toggle.closest("[data-prepaid-mode]");
      if (!wrapper) return;
      const options = toggle.querySelectorAll("[data-mode]");

      function applyMode(mode) {
        if (mode !== "monthly" && mode !== "prepaid") mode = "monthly";
        wrapper.setAttribute("data-prepaid-mode", mode);
        options.forEach(function (opt) {
          opt.setAttribute(
            "aria-checked",
            opt.getAttribute("data-mode") === mode ? "true" : "false"
          );
        });
        const cards = wrapper.querySelectorAll(".cclp-tier-card--quantity");
        cards.forEach(function (card) {
          const permalink = card.getAttribute("data-" + mode + "-permalink");
          const extras = card.getAttribute("data-" + mode + "-extras-csv");
          const planId = card.getAttribute("data-" + mode + "-selling-plan-id");
          if (permalink) {
            card.setAttribute("data-permalink", permalink);
            card.setAttribute("href", permalink);
          }
          if (extras !== null) {
            card.setAttribute("data-extras-csv", extras);
          }
          if (planId !== null) {
            card.setAttribute("data-selling-plan-id", planId);
          }
        });
        // Update main CTA href from currently-selected tier card
        const selected =
          wrapper.querySelector(".cclp-tier-card--selected") ||
          wrapper.querySelector(".cclp-tier-card--quantity");
        if (selected) {
          const url = selected.getAttribute("data-permalink");
          const mainCta = wrapper.querySelector("[data-cclp-main-cta]");
          if (mainCta && url) {
            mainCta.setAttribute("href", url);
          }
        }
        try {
          localStorage.setItem("cclp-prepaid-mode", mode);
        } catch (e) {}
      }

      options.forEach(function (opt) {
        opt.addEventListener("click", function () {
          applyMode(opt.getAttribute("data-mode"));
        });
        opt.addEventListener("keydown", function (e) {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            applyMode(opt.getAttribute("data-mode"));
          }
        });
      });

      // Restore saved mode (only honor if section author hasn't forced default elsewhere).
      try {
        const saved = localStorage.getItem("cclp-prepaid-mode");
        if (saved === "monthly" || saved === "prepaid") {
          applyMode(saved);
        } else {
          // Apply the section's default (already in DOM) so cards' data attrs
          // stay in sync with the displayed price.
          applyMode(wrapper.getAttribute("data-prepaid-mode") || "monthly");
        }
      } catch (e) {
        applyMode(wrapper.getAttribute("data-prepaid-mode") || "monthly");
      }
    });
  }
})();
